import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import type { Filters } from "../types";

const DEFAULT_FILTERS: Filters = {
  search: "",
  scope: "",
  level: "",
  discipline: "",
  category: "",
  revision: "",
  parent: "",
  completion: "",
  testCoverage: "",
  tag: "",
  owner: "",
  testType: "",
  testStatus: "",
  testHasIssues: "",
  testTag: "",
  testSuite: "",
};

interface FilterContextValue {
  filters: Filters;
  setFilter: (key: keyof Filters, value: string) => void;
  clearFilters: () => void;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  sortField: string;
  sortDir: "asc" | "desc";
  setSort: (field: string, dir: "asc" | "desc") => void;
}

const FilterContext = createContext<FilterContextValue | null>(null);

export function FilterProvider({ children }: { children: ReactNode }) {
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState("id");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const setFilter = useCallback((key: keyof Filters, value: string) => {
    setFilters(f => ({ ...f, [key]: value }));
    setPage(0);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setPage(0);
  }, []);

  const setSort = useCallback((field: string, dir: "asc" | "desc") => {
    setSortField(field);
    setSortDir(dir);
  }, []);

  return (
    <FilterContext.Provider value={{ filters, setFilter, clearFilters, page, pageSize, setPage, setPageSize, sortField, sortDir, setSort }}>
      {children}
    </FilterContext.Provider>
  );
}

export function useFilters(): FilterContextValue {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error("useFilters must be used within FilterProvider");
  return ctx;
}
